// client/src/api/gameService.js
import api from './api';
import socketService from './socketService';
import { v4 as uuidv4 } from 'uuid';

/**
 * Crée une nouvelle partie contre l'IA
 * @param {string} deckId - ID du deck du joueur
 * @param {Object} options - Options supplémentaires (difficulté, etc.)
 * @returns {Promise<Object>} - Informations sur la partie créée
 */
export const createAIGame = async (deckId, options = {}) => {
  try {
    console.log("[GAME_SERVICE] Création de partie IA avec:", { deckId, ...options });
    
    console.log("[GAME_SERVICE] Données envoyées:", {
      deckId,
      isAI: true,
      difficulty: options.difficulty || 'medium'
    });

    const response = await api.post('/games', {
      deckId,
      isAI: true,
      difficulty: options.difficulty || 'medium'
    });
    
    console.log("[GAME_SERVICE] Réponse de création IA:", response.data);
    
    // Si la création a réussi, rejoindre la salle socket
    if (response.data && response.data.gameId) {
      try {
        socketService.joinGame(response.data.gameId);
      } catch (socketError) {
        console.warn("[GAME_SERVICE] Erreur socket ignorée:", socketError);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("[GAME_SERVICE] Erreur lors de la création de partie IA:", error);
    throw error;
  }
}

/**
 * Vérifie l'état d'une partie avec plusieurs tentatives
 * @param {string} gameId - ID de la partie
 * @param {number} maxRetries - Nombre maximal de tentatives (défaut: 3)
 * @param {number} delay - Délai entre les tentatives en ms (défaut: 1000)
 * @returns {Promise<Object>} - État de la partie
 */
export const verifyGameState = async (gameId, maxRetries = 3, delay = 1000) => {
  let retries = 0;
  
  const tryGetState = async () => {
    try {
      const result = await getGameState(gameId);
      
      if (result && result.gameState) {
        console.log(`[GAME_SERVICE] État de la partie ${gameId} vérifié avec succès`);
        return result;
      }
      
      throw new Error("État de jeu incomplet");
    } catch (error) {
      retries++;
      
      if (retries >= maxRetries) {
        console.error(`[GAME_SERVICE] Échec après ${maxRetries} tentatives:`, error);
        throw error;
      }
      
      console.warn(`[GAME_SERVICE] Tentative ${retries}/${maxRetries} échouée, nouvelle tentative dans ${delay}ms`);
      
      return new Promise(resolve => {
        setTimeout(() => resolve(tryGetState()), delay);
      });
    }
  };
  
  return tryGetState();
};

// Mode développement : activer le fallback en mode hors ligne si nécessaire
const DEVELOPMENT_MODE = process.env.NODE_ENV === 'development';
const ENABLE_OFFLINE_FALLBACK = DEVELOPMENT_MODE;

// Un mock plus robuste du moteur de jeu pour le développement hors ligne
const gameEngineMock = {
  initializeGame: (playerDeck, opponentDeck) => {
    // Assurer que les decks sont des tableaux
    const pDeck = Array.isArray(playerDeck) ? playerDeck : Array(30).fill('fir_cha_001');
    const oDeck = Array.isArray(opponentDeck) ? opponentDeck : Array(30).fill('wat_cha_001');
    
    return {
      playerLife: 30,
      opponentLife: 30,
      playerEnergy: 1,
      opponentEnergy: 1,
      playerDeck: pDeck.slice(5), // Les 5 premières cartes vont dans la main
      opponentDeck: oDeck.slice(5),
      playerHand: pDeck.slice(0, 5),
      opponentHand: oDeck.slice(0, 5),
      playerField: [],
      opponentField: [],
      playerGraveyard: [],
      opponentGraveyard: [],
      turn: 1,
      phase: 'draw',
      activePlayer: 'player',
      log: [{ type: 'GAME_START', message: 'La partie commence!' }],
      lastAction: null
    };
  },
  
  nextPhase: (state) => {
    // Gestion plus robuste des phases
    const phases = ['draw', 'main1', 'battle', 'main2', 'end'];
    const currentPhaseIndex = phases.indexOf(state.phase);
    const nextPhaseIndex = (currentPhaseIndex + 1) % phases.length;
    const newPhase = phases[nextPhaseIndex];
    
    // Change de joueur à la fin du tour
    let newActivePlayer = state.activePlayer;
    let newTurn = state.turn;
    
    if (newPhase === 'draw') {
      newActivePlayer = state.activePlayer === 'player' ? 'opponent' : 'player';
      
      // Incrémenter le tour si c'est au tour du joueur
      if (newActivePlayer === 'player') {
        newTurn = state.turn + 1;
      }
    }
    
    return {
      ...state,
      phase: newPhase,
      activePlayer: newActivePlayer,
      turn: newTurn,
      log: [...state.log, { 
        type: 'PHASE_CHANGE', 
        message: `Phase: ${newPhase}`,
        player: newActivePlayer 
      }]
    };
  },
  
  // Autres méthodes mock pour le développement
  summonCharacter: (state, player, cardId, position) => {
    console.log(`[MOCK] ${player} invoque ${cardId} à la position ${position}`);
    
    // Simuler l'invocation d'un personnage
    const fieldKey = player === 'player' ? 'playerField' : 'opponentField';
    const handKey = player === 'player' ? 'playerHand' : 'opponentHand';
    const energyKey = player === 'player' ? 'playerEnergy' : 'opponentEnergy';
    
    // Trouver la carte dans la main
    const handIndex = state[handKey].findIndex(card => card.id === cardId);
    if (handIndex === -1) return state;
    
    const card = state[handKey][handIndex];
    if (state[energyKey] < card.cost) return state;
    
    // Retirer la carte de la main
    const newHand = [...state[handKey]];
    newHand.splice(handIndex, 1);
    
    // Ajouter la carte au terrain
    const fieldCard = {
      ...card,
      position,
      zone: 'monster',
      hasAttacked: false
    };
    
    return {
      ...state,
      [handKey]: newHand,
      [fieldKey]: [...state[fieldKey], fieldCard],
      [energyKey]: state[energyKey] - card.cost,
      log: [...state.log, {
        type: 'SUMMON',
        message: `${card.name} a été invoqué!`,
        card: card.name,
        player
      }]
    };
  },
  
  // Mock d'attaque simplifiée
  attack: (state, attackerInfo, defenderInfo) => {
    console.log(`[MOCK] Attaque de ${attackerInfo.player} position ${attackerInfo.cardIndex}`);
    return {
      ...state,
      log: [...state.log, {
        type: 'ATTACK',
        message: 'Attaque effectuée'
      }]
    };
  }
};

// Mock de l'IA amélioré
const aiPlayerMock = {
  playAITurn: (gameState) => {
    console.log('[MOCK] L\'IA joue son tour');
    
    // Copier l'état actuel
    let newState = {...gameState};
    
    // Augmenter l'énergie de l'IA au début de son tour
    newState.opponentEnergy = Math.min(10, newState.turn);
    
    // Phase de pioche (simulée)
    if (newState.opponentDeck.length > 0) {
      const drawnCard = newState.opponentDeck[0];
      newState = {
        ...newState,
        opponentHand: [...newState.opponentHand, drawnCard],
        opponentDeck: newState.opponentDeck.slice(1),
        log: [...newState.log, {
          type: 'DRAW',
          message: 'L\'IA pioche une carte',
          player: 'opponent'
        }]
      };
    }
    
    // Simuler un monstre joué par l'IA si elle a assez d'énergie
    if (newState.opponentHand.length > 0 && newState.opponentEnergy >= 3) {
      const cardIndex = 0; // Première carte de la main
      const card = newState.opponentHand[cardIndex];
      
      if (card.type === 'CHARACTER' || card.type === 'PERSONNAGE') {
        // Trouver une position libre
        let freePosition = -1;
        const occupiedPositions = newState.opponentField
          .filter(c => c.zone === 'monster')
          .map(c => c.position);
        
        for (let pos = 0; pos < 5; pos++) {
          if (!occupiedPositions.includes(pos)) {
            freePosition = pos;
            break;
          }
        }
        
        if (freePosition !== -1) {
          // Invoquer le monstre
          newState.opponentHand.splice(cardIndex, 1);
          newState.opponentField.push({
            ...card,
            position: freePosition,
            zone: 'monster',
            hasAttacked: false
          });
          newState.opponentEnergy -= card.cost;
          
          newState.log.push({
            type: 'SUMMON',
            message: `L'IA invoque un monstre en position ${freePosition}`,
            card: card.name || 'monstre',
            player: 'opponent'
          });
        }
      }
    }
    
    // Simuler les différentes phases
    ['main1', 'battle', 'main2', 'end'].forEach(phase => {
      newState = {
        ...newState,
        phase,
        log: [...newState.log, {
          type: 'PHASE_CHANGE',
          message: `Phase: ${phase}`,
          player: 'opponent'
        }]
      };
    });
    
    // Retourner au joueur
    return {
      ...newState,
      activePlayer: 'player',
      phase: 'draw',
      turn: newState.turn + 1,
      log: [...newState.log, {
        type: 'PHASE_CHANGE',
        message: 'Phase: draw',
        player: 'player'
      }]
    };
  }
};

// Stockage local pour les jeux hors ligne
const offlineGames = new Map();

// Créer une nouvelle partie
const createGame = async (deckId, options = {}) => {
  try {
    const { isAI = false, difficulty = 'medium' } = options;
    
    console.log("[GAME_SERVICE] Création avec:", { deckId, isAI, difficulty });
    
    // Tenter de créer la partie via l'API
    const response = await api.post('/games', {
      deckId,
      isAI,
      difficulty
    });
    
    console.log("[GAME_SERVICE] Réponse de création:", response.data);
    
    // Si c'est une partie en ligne, rejoindre la salle socket
    if (response.data && response.data.gameId) {
      socketService.joinGame(response.data.gameId);
    }
    
    return response.data;
  } catch (error) {
    console.error("[GAME_SERVICE] Erreur de création:", error);
    
    // Mode fallback pour le développement
    if (ENABLE_OFFLINE_FALLBACK) {
      console.warn("[GAME_SERVICE] Utilisation du mode fallback pour la création de partie");
      
      // Générer un ID unique
      const gameId = options.isAI ? `ai_offline_${uuidv4()}` : `pvp_offline_${uuidv4()}`;
      
      // Créer un état de jeu par défaut
      const playerDeck = Array(30).fill('fir_cha_001');
      const opponentDeck = Array(30).fill('wat_cha_001');
      
      // Initialiser l'état du jeu
      const gameState = gameEngineMock.initializeGame(playerDeck, opponentDeck);
      
      // Sauvegarder la partie en mode hors ligne
      offlineGames.set(gameId, {
        id: gameId,
        isAIGame: options.isAI,
        difficulty: options.isAI ? options.difficulty : null,
        gameState
      });
      
      // Retourner une réponse similaire à celle de l'API
      return {
        success: true,
        gameId,
        isAIGame: options.isAI,
        offline: true
      };
    }
    
    throw error;
  }
};

// Rejoindre une partie existante
const joinGame = async (gameId, deckId) => {
  try {
    // Vérifier que gameId est valide avant de faire la requête
    if (!gameId || gameId === 'undefined') {
      console.error('[GAME_SERVICE] Tentative de rejoindre une partie avec un ID invalide:', gameId);
      return { 
        success: false, 
        message: 'ID de partie invalide' 
      };
    }
    
    // Vérifier si c'est une partie hors ligne ou IA (format de l'ID)
    const isAIGame = gameId.startsWith('ai_');
    const isOfflineGame = gameId.includes('offline');
    
    if (isOfflineGame) {
      console.warn('[GAME_SERVICE] Rejoindre une partie hors ligne:', gameId);
      return {
        success: true,
        message: 'Joined offline game',
        gameId,
        offline: true
      };
    }

    console.log(`[GAME_SERVICE] Tentative de rejoindre la partie ${gameId} avec le deck ${deckId}`);
    
    // Si c'est une partie IA, le joueur est déjà le créateur, pas besoin de rejoindre
    if (isAIGame) {
      console.log(`[GAME_SERVICE] Partie IA détectée, pas besoin de rejoindre`);
      socketService.joinGame(gameId);
      return {
        success: true,
        message: 'Joined AI game',
        gameId,
        isAIGame: true
      };
    }
    
    const response = await api.post('/games/join', {
      gameId,
      deckId
    });
    
    // Rejoindre la salle socket pour les mises à jour en temps réel
    socketService.joinGame(gameId);
    
    return response.data;
  } catch (error) {
    console.warn('[GAME_SERVICE] Erreur lors de la tentative de rejoindre la partie:', error);
    
    // Mode fallback pour le développement
    if (ENABLE_OFFLINE_FALLBACK) {
      console.warn("[GAME_SERVICE] Utilisation du mode fallback pour rejoindre une partie");
      
      return {
        success: true,
        message: 'Joined game in offline mode',
        gameId,
        offline: true
      };
    }
    
    return { success: false, message: 'Erreur lors de la connexion à la partie' };
  }
};

// Récupérer l'état d'une partie
const getGameState = async (gameId) => {
  try {
    // Vérification supplémentaire
    if (!gameId || gameId === 'undefined') {
      console.error("[GAME_SERVICE] ID de partie invalide:", gameId);
      return { 
        success: false, 
        message: 'ID de partie invalide',
        gameState: null
      };
    }
    
    console.log("[GAME_SERVICE] Récupération d'état pour:", gameId);
    
    // Si c'est une partie hors ligne, la récupérer directement
    if (ENABLE_OFFLINE_FALLBACK && gameId.includes('offline')) {
      const offlineGame = offlineGames.get(gameId);
      if (offlineGame) {
        return {
          success: true,
          gameState: offlineGame.gameState,
          isAIGame: offlineGame.isAIGame,
          offline: true
        };
      }
    }
    
    const response = await api.get(`/games/${gameId}`);
    
    // Vérification des données reçues
    if (!response.data || !response.data.gameState) {
      console.warn("[GAME_SERVICE] Réponse sans état de jeu valide:", response.data);
      return {
        success: false,
        message: response.data?.message || 'État du jeu non disponible',
        gameState: null,
        recreated: response.data?.recreated || false
      };
    }
    
    console.log("[GAME_SERVICE] État récupéré avec succès pour", gameId);
    
    // Mettre à jour l'état hors ligne si nécessaire
    if (ENABLE_OFFLINE_FALLBACK && response.data.gameState) {
      try {
        // Sauvegarder une copie en mode hors ligne pour les fallbacks
        const offlineGameData = {
          id: gameId,
          isAIGame: response.data.isAIGame || gameId.startsWith('ai_'),
          gameState: response.data.gameState,
          createdAt: new Date()
        };
        offlineGames.set(gameId, offlineGameData);
        console.log(`[GAME_SERVICE] État sauvegardé en cache local pour ${gameId}`);
      } catch (e) {
        console.warn(`[GAME_SERVICE] Impossible de sauvegarder l'état en cache:`, e);
      }
    }
    
    return response.data;
  } catch (error) {
    // Gestion spécifique des erreurs 404
    if (error.response && error.response.status === 404) {
      console.warn(`[GAME_SERVICE] Partie ${gameId} non trouvée`);
      
      // En mode développement, essayer de récupérer depuis le cache hors ligne
      if (ENABLE_OFFLINE_FALLBACK) {
        const offlineGame = offlineGames.get(gameId);
        if (offlineGame) {
          console.log(`[GAME_SERVICE] Récupération depuis le cache local pour ${gameId}`);
          return {
            success: true,
            gameState: offlineGame.gameState,
            isAIGame: offlineGame.isAIGame,
            offline: true
          };
        }
      }
      
      return {
        success: false,
        message: 'Partie non trouvée',
        gameState: null
      };
    }
    
    console.error("[GAME_SERVICE] Erreur de récupération pour", gameId, error);
    
    // Essayer le mode hors ligne en cas d'erreur
    if (ENABLE_OFFLINE_FALLBACK) {
      const offlineGame = offlineGames.get(gameId);
      if (offlineGame) {
        console.log(`[GAME_SERVICE] Fallback vers le cache local pour ${gameId}`);
        return {
          success: true,
          gameState: offlineGame.gameState,
          isAIGame: offlineGame.isAIGame,
          offline: true
        };
      }
    }
    
    throw error;
  }
};

// Effectuer une action dans une partie
const performAction = async (gameId, action, data = {}) => {
  try {
    // Vérifier si c'est une partie hors ligne
    if (ENABLE_OFFLINE_FALLBACK && gameId.includes('offline')) {
      console.warn('[SERVICE] Action sur partie hors ligne:', gameId, action);
      const game = offlineGames.get(gameId);
      
      if (!game) {
        return { 
          success: false,
          message: 'Partie hors ligne non trouvée'
        };
      }
      
      let updatedState = { ...game.gameState };
      
      // Traiter l'action en mode hors ligne
      switch (action) {
        case 'END_PHASE':
        case 'NEXT_PHASE':
          updatedState = gameEngineMock.nextPhase(updatedState);
          break;
        case 'SUMMON':
          if (data.cardId && data.position !== undefined) {
            updatedState = gameEngineMock.summonCharacter(
              updatedState,
              'player',
              data.cardId,
              data.position
            );
          }
          break;
        case 'ATTACK':
          const attackerInfo = {
            player: 'player',
            cardIndex: data.position !== undefined ? data.position : 0
          };
          
          const defenderInfo = data.targetPosition !== undefined
            ? { player: 'opponent', cardIndex: data.targetPosition }
            : null;
          
          updatedState = gameEngineMock.attack(updatedState, attackerInfo, defenderInfo);
          break;
        default:
          console.warn(`[OFFLINE] Action non prise en charge: ${action}`);
          // Aucune modification à l'état pour les actions non reconnues
          break;
      }
      
      // Si c'est une partie contre l'IA et que c'est au tour de l'IA
      if (game.isAIGame && updatedState.activePlayer === 'opponent') {
        console.log('[OFFLINE] Tour de l\'IA après action du joueur.');
        // Ajout d'un délai artificiel pour simuler réflexion de l'IA
        await new Promise(resolve => setTimeout(resolve, 500));
        updatedState = aiPlayerMock.playAITurn(updatedState);
        
        // S'assurer que l'énergie de l'IA est mise à jour correctement
        if (updatedState.opponentEnergy < 1) {
          updatedState.opponentEnergy = Math.min(10, Math.ceil(updatedState.turn / 2));
        }
      }
      
      // Mettre à jour l'état de la partie hors ligne
      game.gameState = updatedState;
      offlineGames.set(gameId, game);
      
      return { 
        success: true,
        gameState: updatedState,
        offline: true
      };
    }
    
    console.log(`[GAME_SERVICE] Exécution action ${action} sur partie ${gameId}`, data);
    // R34g3 : currentState (état complet, potentiellement >100 ko) n'est utile
    // qu'au fallback offline local — ne JAMAIS l'envoyer au serveur, sinon
    // body-parser rejette la requête (PayloadTooLarge → 500 pour le client).
    const payload = { action, ...data };
    if (!gameId.includes('offline')) delete payload.currentState;
    const response = await api.post(`/games/${gameId}/action`, payload);
    
    console.log(`[GAME_SERVICE] Réponse pour action ${action}:`, response.data);
    return response.data;
  } catch (error) {
    console.warn('Failed to perform action:', error);
    
    // Fallback mock UNIQUEMENT pour les parties locales (id 'offline').
    // Une erreur serveur sur une partie ai_/pvp_ doit remonter à l'utilisateur —
    // remplacer l'état par un mock désynchroniserait silencieusement le jeu.
    if (ENABLE_OFFLINE_FALLBACK && gameId.includes('offline')) {
      console.warn("[GAME_SERVICE] Traitement de l'action en mode hors ligne après erreur");
      
      let updatedState = data.currentState || {};
      
      switch (action) {
        case 'NEXT_PHASE':
        case 'END_PHASE':
          updatedState = gameEngineMock.nextPhase(updatedState);
          break;
        // Autres cas traités localement...
        default:
          // Aucune modification pour les actions non reconnues
          break;
      }
      
      // Forcer le tour de l'IA si nécessaire
      if (updatedState.activePlayer === 'opponent' && gameId.includes('ai')) {
        console.log('[OFFLINE] Forçage du tour de l\'IA après erreur');
        updatedState = aiPlayerMock.playAITurn(updatedState);
      }
      
      return { 
        success: true,
        gameState: updatedState,
        offline: true
      };
    }
    
    throw error;
  }
};

// Abandonner une partie
const surrenderGame = async (gameId, options = {}) => {
  try {
    // Option pour sauter l'appel au serveur (utile pour éviter les boucles)
    if (options.skipServerCall) {
      console.log(`[GAME_SERVICE] Abandon local de la partie ${gameId} sans appel serveur`);
      return { success: true, message: 'Partie abandonnée localement' };
    }

    console.log(`[GAME_SERVICE] Tentative d'abandon de la partie: ${gameId}`);
    
    // Vérifier si c'est une partie hors ligne
    if (ENABLE_OFFLINE_FALLBACK && gameId.includes('offline')) {
      console.warn('[SERVICE] Abandon d\'une partie hors ligne:', gameId);
      
      // Supprimer la partie du stockage hors ligne
      offlineGames.delete(gameId);
      
      return {
        success: true,
        message: 'Partie hors ligne abandonnée',
        offline: true
      };
    }
    
    const response = await api.post(`/games/${gameId}/surrender`);
    
    // Quitter la salle socket
    socketService.leaveGame(gameId, { skipSurrender: true });
    
    return response.data;
  } catch (error) {
    console.warn('Failed to surrender game:', error);
    
    // Mode fallback
    if (ENABLE_OFFLINE_FALLBACK) {
      return { 
        success: true, 
        message: 'Partie abandonnée (mode hors ligne)',
        offline: true 
      };
    }
    
    return { success: true, message: 'Partie abandonnée' };
  }
};

// Récupérer la liste des parties disponibles
const findGames = async () => {
  try {
    const response = await api.get('/games');
    return response.data;
  } catch (error) {
    console.warn('Failed to get available games:', error);
    
    // Mode fallback
    if (ENABLE_OFFLINE_FALLBACK) {
      return { 
        success: true,
        games: [],
        offline: true
      };
    }
    
    return { games: [] };
  }
};

// Alias pour compatibilité
const getAvailableGames = findGames;

// R30 : file d'attente classée (matchmaking par Elo)
// R34a/c : mode 'ranked' (Elo en jeu) ou 'casual', mise optionnelle (wager)
const joinRankedQueue = async (deckId, options = {}) => {
  const response = await api.post('/games/queue', {
    deckId,
    mode: options.mode || 'ranked',
    stake: options.stake || 0,
  });
  return response.data;
};

const leaveRankedQueue = async () => {
  try {
    const response = await api.post('/games/queue/leave');
    return response.data;
  } catch (e) {
    return { success: false };
  }
};

// R34g : confirmation / refus du duel proposé
const confirmMatch = async (gameId) => {
  const response = await api.post(`/games/${gameId}/confirm`);
  return response.data;
};
const declineMatch = async (gameId) => {
  try {
    const response = await api.post(`/games/${gameId}/decline`);
    return response.data;
  } catch (e) { return { success: false }; }
};

// R34cc : défier le même adversaire en revanche (2 défis max, 3 duels/série)
const challengeRematch = async (gameId) => {
  const response = await api.post('/games/rematch/challenge', { gameId });
  return response.data;
};

const getQueueStatus = async () => {
  try {
    const response = await api.get('/games/queue/status');
    return response.data;
  } catch (e) {
    return { inQueue: false };
  }
};

// Simule le moteur de jeu côté client pendant le développement
const getGameEngine = () => gameEngineMock;

// Simule l'IA côté client pendant le développement
const getAIPlayer = () => aiPlayerMock;

const GameService = {
  createGame,
  createAIGame,
  joinGame,
  getGameState,
  performAction,
  surrenderGame,
  findGames,
  getAvailableGames,
  getGameEngine,
  getAIPlayer
};

// Export des fonctions individuelles pour compatibilité
export {
  createGame,
  joinGame,
  joinRankedQueue,
  leaveRankedQueue,
  getQueueStatus,
  confirmMatch,
  declineMatch,
  challengeRematch,
  getGameState,
  performAction,
  surrenderGame,
  findGames,
  getAvailableGames,
  getGameEngine,
  getAIPlayer
};

export default GameService;